import { FunctionComponent, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import CircularProgress from '@mui/material/CircularProgress';
import { userApi, ApiError } from '../../../services/api';
import styles from './ProfilePaymentMethodsScreen.module.css';

interface PaymentMethod {
  id: string;
  type: 'visa' | 'mastercard' | 'amex' | 'discover';
  last4: string;
  expiryMonth: number;
  expiryYear: number;
  isDefault: boolean;
  holderName: string;
}

interface AddPaymentFormData {
  cardNumber: string;
  expiryMonth: string;
  expiryYear: string;
  cvv: string;
  holderName: string;
  zipCode: string;
}

const ProfilePaymentMethodsScreen: FunctionComponent = () => {
  const navigate = useNavigate();
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<AddPaymentFormData>({
    cardNumber: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',
    holderName: '',
    zipCode: ''
  });
  const [formErrors, setFormErrors] = useState<Partial<AddPaymentFormData>>({});

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  const fetchPaymentMethods = async () => {
    try {
      const result = await userApi.getPaymentMethods();
      setPaymentMethods(result);
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      if (error instanceof ApiError) {
        console.error('API Error:', error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getCardIcon = (type: string) => {
    switch (type) {
      case 'visa':
        return '💳 Visa';
      case 'mastercard':
        return '💳 Mastercard';
      case 'amex':
        return '💳 Amex';
      case 'discover':
        return '💳 Discover';
      default:
        return '💳 Card';
    }
  };

  const formatCardNumber = (value: string) => {
    const cleaned = value.replace(/\s+/g, '');
    const match = cleaned.match(/.{1,4}/g);
    return match ? match.join(' ') : cleaned;
  };

  const handleInputChange = (field: keyof AddPaymentFormData, value: string) => {
    let formattedValue = value;
    
    if (field === 'cardNumber') {
      formattedValue = formatCardNumber(value.replace(/\D/g, ''));
    } else if (field === 'expiryMonth' || field === 'expiryYear') {
      formattedValue = value.replace(/\D/g, '');
    } else if (field === 'cvv') {
      formattedValue = value.replace(/\D/g, '').slice(0, 4);
    } else if (field === 'zipCode') {
      formattedValue = value.replace(/\D/g, '').slice(0, 5);
    }
    
    setFormData(prev => ({ ...prev, [field]: formattedValue }));
    
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const errors: Partial<AddPaymentFormData> = {};
    
    if (!formData.cardNumber || formData.cardNumber.replace(/\s/g, '').length < 13) {
      errors.cardNumber = 'Please enter a valid card number';
    }
    
    const month = parseInt(formData.expiryMonth);
    if (!formData.expiryMonth || month < 1 || month > 12) {
      errors.expiryMonth = 'Invalid month';
    }
    
    const year = parseInt(formData.expiryYear);
    const currentYear = new Date().getFullYear() % 100;
    if (!formData.expiryYear || year < currentYear) {
      errors.expiryYear = 'Invalid year';
    }
    
    if (!formData.cvv || formData.cvv.length < 3) {
      errors.cvv = 'Invalid CVV';
    }
    
    if (!formData.holderName || formData.holderName.trim().length < 3) {
      errors.holderName = 'Please enter cardholder name';
    }
    
    if (!formData.zipCode || formData.zipCode.length !== 5) {
      errors.zipCode = 'Invalid ZIP code';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddPayment = async () => {
    if (!validateForm()) {
      return;
    }
    
    setIsAdding(true);
    
    try {
      await userApi.addPaymentMethod(formData);
      
      await fetchPaymentMethods();
      setShowAddForm(false);
      setFormData({
        cardNumber: '',
        expiryMonth: '',
        expiryYear: '',
        cvv: '',
        holderName: '',
        zipCode: ''
      });
    } catch (error) {
      console.error('Error adding payment method:', error);
      if (error instanceof ApiError) {
        console.error('API Error:', error.message);
      }
    } finally {
      setIsAdding(false);
    }
  };

  const handleSetDefault = async (methodId: string) => {
    try {
      await userApi.setDefaultPaymentMethod(methodId);
      await fetchPaymentMethods();
    } catch (error) {
      console.error('Error setting default payment method:', error);
      if (error instanceof ApiError) {
        console.error('API Error:', error.message);
      }
    }
  };

  const handleDelete = async (methodId: string) => {
    if (!window.confirm('Are you sure you want to remove this payment method?')) {
      return;
    }
    
    setDeletingId(methodId);
    
    try {
      await userApi.deletePaymentMethod(methodId);
      await fetchPaymentMethods();
    } catch (error) {
      console.error('Error deleting payment method:', error);
      if (error instanceof ApiError) {
        console.error('API Error:', error.message);
      }
    } finally {
      setDeletingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.profilePaymentMethods}>
        <div className={styles.loadingContainer}>
          <CircularProgress />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.profilePaymentMethods}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate('/profile')}>
          <ArrowBackIcon />
        </button>
        <h1 className={styles.headerTitle}>Payment Methods</h1>
        <div className={styles.headerSpacer} />
      </div>

      <div className={styles.content}>
        <div className={styles.paymentMethodsSection}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Your Payment Methods</h2>
            <button
              className={styles.addButton}
              onClick={() => setShowAddForm(true)}
            >
              <AddIcon />
              Add New
            </button>
          </div>

          {paymentMethods.length === 0 ? (
            <div className={styles.emptyState}>
              <CreditCardIcon className={styles.emptyIcon} />
              <p className={styles.emptyText}>No payment methods saved</p>
              <button
                className={styles.addFirstButton}
                onClick={() => setShowAddForm(true)}
              >
                Add your first payment method
              </button>
            </div>
          ) : (
            <div className={styles.methodsList}>
              {paymentMethods.map(method => (
                <div key={method.id} className={styles.methodCard}>
                  <div className={styles.methodInfo}>
                    <div className={styles.methodHeader}>
                      <span className={styles.cardType}>{getCardIcon(method.type)}</span>
                      {method.isDefault && (
                        <span className={styles.defaultBadge}>Default</span>
                      )}
                    </div>
                    <p className={styles.cardNumber}>•••• •••• •••• {method.last4}</p>
                    <p className={styles.cardDetails}>
                      {method.holderName} • Expires {method.expiryMonth.toString().padStart(2, '0')}/{method.expiryYear}
                    </p>
                  </div>
                  <div className={styles.methodActions}>
                    {!method.isDefault && (
                      <button
                        className={styles.setDefaultButton}
                        onClick={() => handleSetDefault(method.id)}
                      >
                        Set as default
                      </button>
                    )}
                    <button
                      className={styles.deleteButton}
                      onClick={() => handleDelete(method.id)}
                      disabled={deletingId === method.id}
                    >
                      {deletingId === method.id ? (
                        <CircularProgress size={16} />
                      ) : (
                        <DeleteIcon />
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {showAddForm && (
          <div className={styles.addFormOverlay}>
            <div className={styles.addFormModal}>
              <h3 className={styles.modalTitle}>Add Payment Method</h3>
              
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Card Number</label>
                <input
                  type="text"
                  className={`${styles.formInput} ${formErrors.cardNumber ? styles.inputError : ''}`}
                  value={formData.cardNumber}
                  onChange={(e) => handleInputChange('cardNumber', e.target.value)}
                  placeholder="1234 5678 9012 3456"
                  maxLength={19}
                />
                {formErrors.cardNumber && (
                  <span className={styles.errorText}>{formErrors.cardNumber}</span>
                )}
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Expiry Date</label>
                  <div className={styles.expiryInputs}>
                    <input
                      type="text"
                      className={`${styles.formInput} ${styles.monthInput} ${formErrors.expiryMonth ? styles.inputError : ''}`}
                      value={formData.expiryMonth}
                      onChange={(e) => handleInputChange('expiryMonth', e.target.value)}
                      placeholder="MM"
                      maxLength={2}
                    />
                    <span className={styles.expirySeparator}>/</span>
                    <input
                      type="text"
                      className={`${styles.formInput} ${styles.yearInput} ${formErrors.expiryYear ? styles.inputError : ''}`}
                      value={formData.expiryYear}
                      onChange={(e) => handleInputChange('expiryYear', e.target.value)}
                      placeholder="YY"
                      maxLength={2}
                    />
                  </div>
                  {(formErrors.expiryMonth || formErrors.expiryYear) && (
                    <span className={styles.errorText}>
                      {formErrors.expiryMonth || formErrors.expiryYear}
                    </span>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>CVV</label>
                  <input
                    type="text"
                    className={`${styles.formInput} ${formErrors.cvv ? styles.inputError : ''}`}
                    value={formData.cvv}
                    onChange={(e) => handleInputChange('cvv', e.target.value)}
                    placeholder="123"
                    maxLength={4}
                  />
                  {formErrors.cvv && (
                    <span className={styles.errorText}>{formErrors.cvv}</span>
                  )}
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Cardholder Name</label>
                <input
                  type="text"
                  className={`${styles.formInput} ${formErrors.holderName ? styles.inputError : ''}`}
                  value={formData.holderName}
                  onChange={(e) => handleInputChange('holderName', e.target.value)}
                  placeholder="John Doe"
                />
                {formErrors.holderName && (
                  <span className={styles.errorText}>{formErrors.holderName}</span>
                )}
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>ZIP Code</label>
                <input
                  type="text"
                  className={`${styles.formInput} ${formErrors.zipCode ? styles.inputError : ''}`}
                  value={formData.zipCode}
                  onChange={(e) => handleInputChange('zipCode', e.target.value)}
                  placeholder="12345"
                  maxLength={5}
                />
                {formErrors.zipCode && (
                  <span className={styles.errorText}>{formErrors.zipCode}</span>
                )}
              </div>

              <div className={styles.modalActions}>
                <button
                  className={styles.cancelButton}
                  onClick={() => setShowAddForm(false)}
                  disabled={isAdding}
                >
                  Cancel
                </button>
                <button
                  className={styles.confirmButton}
                  onClick={handleAddPayment}
                  disabled={isAdding}
                >
                  {isAdding ? (
                    <>
                      <CircularProgress size={20} color="inherit" />
                      <span>Adding...</span>
                    </>
                  ) : (
                    'Add Payment Method'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className={styles.infoSection}>
          <h3 className={styles.infoTitle}>
            <InfoIcon className={styles.infoIcon} />
            Payment Security
          </h3>
          <p className={styles.infoText}>
            Your payment information is encrypted and securely stored. We never store your full card number or CVV.
          </p>
          <p className={styles.infoText}>
            Payment methods are used for subscription billing and any premium services you purchase.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProfilePaymentMethodsScreen;